/**
 * Offline game claims. A game played offline is queued here (localStorage, so it survives the app being closed)
 * and submitted to the server once we are online and signed in. The server re-runs the game log against the
 * real table rules before it moves any coins, and each claim id is applied at most once, so flushing is always
 * safe to retry.
 *
 * Coins are tracked provisionally on the device while offline: starting a table at once takes the entry fee off
 * the shown balance, finishing it adds the result. Those provisional amounts live on the queued claims, so the
 * shown balance is always `server balance + pending claims` — across reloads and until the server confirms.
 * A game that was started but never finished (the app was closed) is submitted as a forfeit of its stake.
 */
import type { OfflineClaimsResponse } from '@cardhub/protocol';
import { api, ApiError } from '../api/client';
import { useSession } from '../store/session';
import { toast } from '../store/ui';

const KEY = 'th.offlineClaims';
/** A game log is tens of KB: keep requests well below the server's body limit. */
const BATCH = 2;

export interface PendingClaim {
  id: string;
  userId: string;
  /** Absent on claims queued by older app versions (Call Break only, paid by fixed reward). */
  tableConfigId?: string;
  log?: unknown[];
  /** Still being played; if the app was closed meanwhile it is submitted as a forfeit. */
  playing?: boolean;
  abandoned?: boolean;
  /** Coins this claim has provisionally added to (+) or taken from (−) the shown balance. */
  provisional?: number;
}

function read(): PendingClaim[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? (v as PendingClaim[]) : [];
  } catch {
    return [];
  }
}

function write(list: PendingClaim[]): void {
  try {
    if (list.length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* storage full or blocked: the claim is simply lost, never a crash */
  }
}

/** The game being played right now (its claim is not a forfeit yet). */
let activeId: string | null = null;
export function setActiveClaim(id: string | null): void {
  activeId = id;
}

export function pendingClaimCount(): number {
  const uid = useSession.getState().user?.id;
  return read().filter((c) => c.userId === uid && !(c.playing && c.id === activeId)).length;
}

/** Coins the queued claims have provisionally added to (+) or taken from (−) the server's balance. */
export function pendingDelta(userId: string | undefined): number {
  if (!userId) return 0;
  return read().reduce((n, c) => (c.userId === userId ? n + (c.provisional ?? 0) : n), 0);
}

function shift(by: number): void {
  if (by !== 0) useSession.setState((s) => ({ balance: s.balance + by }));
}

/** A table was sat down at: queue it (as a forfeit unless it gets finished) and take the stake off the balance. */
export function beginClaim(claim: { id: string; userId: string; tableConfigId: string; stake: number }): void {
  write([...read().filter((c) => c.id !== claim.id), { id: claim.id, userId: claim.userId, tableConfigId: claim.tableConfigId, playing: true, provisional: -claim.stake }]);
  activeId = claim.id;
  shift(-claim.stake);
}

/** The game ended (or was left): attach its log and the provisional net result. */
export function completeClaim(id: string, r: { log: unknown[]; net: number; abandoned?: boolean }): void {
  const list = read();
  const cur = list.find((c) => c.id === id);
  if (!cur) return;
  const before = cur.provisional ?? 0;
  const next: PendingClaim = { id: cur.id, userId: cur.userId, ...(cur.tableConfigId ? { tableConfigId: cur.tableConfigId } : {}), log: r.log, provisional: r.net, ...(r.abandoned ? { abandoned: true } : {}) };
  write(list.map((c) => (c.id === id ? next : c)));
  if (activeId === id) activeId = null;
  shift(r.net - before);
}

/** The game could not be played after all (e.g. it failed to start): drop the claim and give the stake back. */
export function cancelClaim(id: string): void {
  const list = read();
  const cur = list.find((c) => c.id === id);
  if (!cur) return;
  write(list.filter((c) => c.id !== id));
  if (activeId === id) activeId = null;
  shift(-(cur.provisional ?? 0));
}

let flushing = false;

/** Submits queued claims for the signed-in user. Safe to call at any time; does nothing offline or when empty. */
export async function flushClaims(): Promise<void> {
  const session = useSession.getState();
  if (flushing || session.status !== 'authed' || !session.user || !navigator.onLine) return;
  const userId = session.user.id;
  const ready = () => read().filter((c) => c.userId === userId && !(c.playing && c.id === activeId));
  if (ready().length === 0) return;
  flushing = true;
  let net = 0;
  let any = false;
  try {
    for (;;) {
      const batch = ready().slice(0, BATCH);
      if (batch.length === 0) break;
      let res: OfflineClaimsResponse;
      try {
        res = await api.post<OfflineClaimsResponse>('/api/v1/offline/claims', {
          claims: batch.map((c) => ({
            id: c.id,
            ...(c.tableConfigId ? { tableConfigId: c.tableConfigId } : {}),
            ...(c.playing || !c.log ? { forfeit: true } : { log: c.log }),
            ...(c.abandoned ? { abandoned: true } : {}),
          })),
        });
      } catch (e) {
        // A rejected request (validation/too big) would block the queue forever: drop it. Anything else (network, 5xx) retries later.
        if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 401 && e.status !== 429) write(read().filter((c) => !batch.some((b) => b.id === c.id)));
        break;
      }
      const done = new Set(res.results.map((r) => r.id));
      write(read().filter((c) => !done.has(c.id)));
      net += res.results.reduce((n, r) => n + r.coins, 0);
      any = true;
      // The server's balance now includes what was just applied; whatever is still queued is added on top again.
      useSession.setState({ balance: res.balance + pendingDelta(userId) });
    }
  } finally {
    flushing = false;
  }
  if (any && net !== 0) toast(net > 0 ? `Offline games synced: +${net} coins` : `Offline games synced: ${net} coins`, net > 0 ? 'success' : 'info');
  if (any) void useSession.getState().refreshMe().catch(() => undefined);
}
